import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { emailingAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Send,
  Eye,
  Sparkles,
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  Type,
  Image as ImageIcon,
  MousePointerClick,
  Minus,
  Columns,
  Users,
  Filter,
  Search,
  Wand2,
  Mail,
  TestTube,
  Clock,
  CheckCircle,
  FileText,
  Copy,
} from 'lucide-react';

// Types de blocs disponibles
const BLOCK_TYPES = [
  { type: 'text', label: 'Texte', icon: Type, description: 'Paragraphe de texte' },
  { type: 'button', label: 'Bouton', icon: MousePointerClick, description: 'Bouton d\'action' },
  { type: 'image', label: 'Image', icon: ImageIcon, description: 'Image ou bannière' },
  { type: 'spacer', label: 'Espacement', icon: Minus, description: 'Espace vertical' },
  { type: 'divider', label: 'Séparateur', icon: Minus, description: 'Ligne horizontale' },
  { type: 'columns', label: 'Colonnes', icon: Columns, description: '2 ou 3 colonnes' },
];

// Filtres de destinataires
const FILTER_TYPES = [
  { value: 'all', label: 'Tous les contacts', description: 'Tous les contacts avec email' },
  { value: 'devis_status', label: 'Par statut de devis', description: 'Contacts avec devis en attente, envoyé, etc.' },
  { value: 'contact_types', label: 'Par type de contact', description: 'Clients, SNC, Fournisseurs, etc.' },
  { value: 'inactive', label: 'Clients inactifs', description: 'Pas d\'activité depuis X jours' },
  { value: 'new_contacts', label: 'Nouveaux contacts', description: 'Contacts créés récemment' },
  { value: 'manual', label: 'Sélection manuelle', description: 'Choisir les contacts un par un' },
];

const EmailEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Content state
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [contentBlocks, setContentBlocks] = useState([]);
  
  // Recipients state
  const [filterType, setFilterType] = useState('all');
  const [filterParams, setFilterParams] = useState({});
  const [recipientsPreview, setRecipientsPreview] = useState({ recipients: [], total: 0 });
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  
  // Manual selection and exclusion
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [excludedContacts, setExcludedContacts] = useState([]); // Contacts exclus de la sélection automatique
  
  // Dialogs
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  // AI Dialog
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTone, setAiTone] = useState('professionnel');
  const [aiInstructions, setAiInstructions] = useState('');

  // Drag state
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);

  useEffect(() => {
    fetchCampaign();
    fetchTemplates();
  }, [id]);

  useEffect(() => {
    if (filterType && filterType !== 'manual') {
      fetchRecipientsPreview();
    }
  }, [filterType, filterParams]);

  const fetchCampaign = async () => {
    try {
      const response = await emailingAPI.getCampaign(id);
      const data = response.data;
      setCampaign(data);
      setSubject(data.subject || '');
      setPreviewText(data.preview_text || '');
      setContentBlocks(data.content_blocks || []);
      setFilterType(data.filter_type || 'all');
      setFilterParams(data.filter_params || {});
      if (data.filter_type === 'manual' && data.filter_params?.client_ids) {
        // Load selected contacts
        setSelectedContacts(data.filter_params.client_ids);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement de la campagne');
      navigate('/admin/emailing');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipientsPreview = async () => {
    setRecipientsLoading(true);
    try {
      const response = await emailingAPI.previewRecipients(filterType, filterParams);
      setRecipientsPreview(response.data);
    } catch (error) {
      console.error('Erreur prévisualisation:', error);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await emailingAPI.getTemplates();
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const applyTemplate = (template) => {
    setSubject(template.subject || '');
    setPreviewText(template.preview_text || '');
    setContentBlocks(template.content_blocks || []);
    setTemplatesDialogOpen(false);
    toast.success(`Template "${template.name}" appliqué`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await emailingAPI.updateCampaign(id, {
        subject,
        preview_text: previewText,
        content_blocks: contentBlocks,
        filter_type: filterType,
        filter_params: filterType === 'manual' 
          ? { client_ids: selectedContacts }
          : filterParams
      });
      toast.success('Campagne sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateWithAI = async () => {
    setAiLoading(true);
    try {
      const response = await emailingAPI.generateContent(
        campaign.campaign_type,
        null,
        aiTone,
        aiInstructions || null
      );
      
      setSubject(response.data.subject || '');
      setPreviewText(response.data.preview_text || '');
      setContentBlocks(response.data.content_blocks || []);
      
      toast.success('Contenu généré avec succès !');
      setAiDialogOpen(false);
      setAiInstructions('');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSend = async (testMode = false) => {
    setSendLoading(true);
    try {
      // Sauvegarder d'abord
      await handleSave();
      
      const response = await emailingAPI.sendCampaign(
        id,
        testMode,
        testMode ? testEmail : null
      );
      
      if (response.data.success) {
        toast.success(`${response.data.sent} email(s) envoyé(s) sur ${response.data.total}`);
        setSendDialogOpen(false);
        fetchCampaign();
      } else {
        toast.error(response.data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendLoading(false);
    }
  };

  // Block management
  const addBlock = (type) => {
    const newBlock = { type, id: Date.now() };
    
    switch (type) {
      case 'text':
        newBlock.content = '<p>Entrez votre texte ici...</p>';
        break;
      case 'button':
        newBlock.text = 'Cliquez ici';
        newBlock.url = 'https://';
        newBlock.color = '#f59e0b';
        break;
      case 'image':
        newBlock.src = '';
        newBlock.alt = '';
        break;
      case 'spacer':
        newBlock.height = 20;
        break;
      case 'divider':
        break;
      case 'columns':
        newBlock.columns = [{ content: 'Colonne 1' }, { content: 'Colonne 2' }];
        break;
    }
    
    setContentBlocks([...contentBlocks, newBlock]);
  };

  const updateBlock = (index, updates) => {
    const newBlocks = [...contentBlocks];
    newBlocks[index] = { ...newBlocks[index], ...updates };
    setContentBlocks(newBlocks);
  };

  const removeBlock = (index) => {
    setContentBlocks(contentBlocks.filter((_, i) => i !== index));
  };

  const moveBlock = (fromIndex, toIndex) => {
    const newBlocks = [...contentBlocks];
    const [removed] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, removed);
    setContentBlocks(newBlocks);
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveBlock(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Contact search
  const handleSearchContacts = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await emailingAPI.searchContacts(searchQuery);
      setSearchResults(response.data.contacts);
    } catch (error) {
      toast.error('Erreur de recherche');
    }
  };

  const toggleContactSelection = (contactId) => {
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(selectedContacts.filter(id => id !== contactId));
    } else {
      setSelectedContacts([...selectedContacts, contactId]);
    }
  };

  // Render block editor
  const renderBlockEditor = (block, index) => {
    const commonProps = {
      draggable: true,
      onDragStart: (e) => handleDragStart(e, index),
      onDragOver: (e) => handleDragOver(e, index),
      onDragEnd: handleDragEnd,
      className: `p-4 border rounded-lg bg-white ${draggedIndex === index ? 'opacity-50 border-orange-400' : 'border-slate-200'}`
    };

    return (
      <div key={block.id || index} {...commonProps}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
            <Badge variant="outline" className="text-xs">
              {BLOCK_TYPES.find(b => b.type === block.type)?.label || block.type}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-red-500"
            onClick={() => removeBlock(index)}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>

        {block.type === 'text' && (
          <Textarea
            value={block.content || ''}
            onChange={(e) => updateBlock(index, { content: e.target.value })}
            rows={4}
            placeholder="Contenu HTML..."
            className="font-mono text-sm"
          />
        )}

        {block.type === 'button' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Texte</Label>
                <Input
                  value={block.text || ''}
                  onChange={(e) => updateBlock(index, { text: e.target.value })}
                  placeholder="Texte du bouton"
                />
              </div>
              <div>
                <Label className="text-xs">Couleur</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={block.color || '#f59e0b'}
                    onChange={(e) => updateBlock(index, { color: e.target.value })}
                    className="w-12 h-9 p-1"
                  />
                  <Input
                    value={block.color || '#f59e0b'}
                    onChange={(e) => updateBlock(index, { color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">URL</Label>
              <Input
                value={block.url || ''}
                onChange={(e) => updateBlock(index, { url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        )}

        {block.type === 'image' && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">URL de l'image</Label>
              <Input
                value={block.src || ''}
                onChange={(e) => updateBlock(index, { src: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label className="text-xs">Texte alternatif</Label>
              <Input
                value={block.alt || ''}
                onChange={(e) => updateBlock(index, { alt: e.target.value })}
                placeholder="Description de l'image"
              />
            </div>
            {block.src && (
              <img src={block.src} alt={block.alt} className="max-h-32 rounded" />
            )}
          </div>
        )}

        {block.type === 'spacer' && (
          <div>
            <Label className="text-xs">Hauteur (px)</Label>
            <Input
              type="number"
              value={block.height || 20}
              onChange={(e) => updateBlock(index, { height: parseInt(e.target.value) })}
              min={5}
              max={100}
              className="w-24"
            />
          </div>
        )}

        {block.type === 'columns' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              {(block.columns || []).map((col, colIndex) => (
                <div key={colIndex} className="flex-1">
                  <Label className="text-xs">Colonne {colIndex + 1}</Label>
                  <Textarea
                    value={col.content || ''}
                    onChange={(e) => {
                      const newCols = [...(block.columns || [])];
                      newCols[colIndex] = { ...newCols[colIndex], content: e.target.value };
                      updateBlock(index, { columns: newCols });
                    }}
                    rows={3}
                    className="text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newCols = [...(block.columns || []), { content: '' }];
                  if (newCols.length <= 3) updateBlock(index, { columns: newCols });
                }}
                disabled={(block.columns || []).length >= 3}
              >
                + Colonne
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newCols = (block.columns || []).slice(0, -1);
                  if (newCols.length >= 1) updateBlock(index, { columns: newCols });
                }}
                disabled={(block.columns || []).length <= 1}
              >
                - Colonne
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/emailing')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              {campaign?.name}
            </h1>
            <Badge variant="outline">{campaign?.campaign_type}</Badge>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)}>
            <Eye className="w-4 h-4 mr-2" />
            Aperçu
          </Button>
          <Button variant="outline" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setSendDialogOpen(true)}>
            <Send className="w-4 h-4 mr-2" />
            Envoyer
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="content">📝 Contenu</TabsTrigger>
          <TabsTrigger value="recipients">👥 Destinataires</TabsTrigger>
        </TabsList>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          {/* Quick Actions Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Templates Card */}
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <FileText className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">Templates prédéfinis</p>
                      <p className="text-sm text-slate-500">{templates.length} modèles disponibles</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-100"
                    onClick={() => setTemplatesDialogOpen(true)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Utiliser un template
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* AI Generation */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">Génération IA</p>
                      <p className="text-sm text-slate-500">Laissez l'IA créer votre email</p>
                    </div>
                  </div>
                  <Button
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => setAiDialogOpen(true)}
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Générer avec l'IA
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subject & Preview */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Objet et aperçu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Objet de l'email *</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Ex: {nom}, votre devis vous attend !"
                  data-testid="email-subject-input"
                />
                <p className="text-xs text-slate-500">
                  Variables disponibles : {'{nom}'}, {'{prenom}'}, {'{entreprise}'}, {'{montant_devis}'}, {'{numero_devis}'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="preview">Texte de prévisualisation</Label>
                <Input
                  id="preview"
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                  placeholder="Texte visible dans la boîte de réception..."
                  data-testid="email-preview-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Content Blocks */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Block Palette */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Blocs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {BLOCK_TYPES.map((block) => (
                  <Button
                    key={block.type}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => addBlock(block.type)}
                  >
                    <block.icon className="w-4 h-4 mr-2" />
                    {block.label}
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Content Editor */}
            <div className="lg:col-span-2 space-y-3">
              <p className="font-medium text-slate-700">Contenu de l'email</p>
              {contentBlocks.length === 0 ? (
                <Card className="border-dashed border-slate-300 bg-slate-50">
                  <CardContent className="p-8 text-center text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun bloc de contenu</p>
                    <p className="text-sm mt-1">Ajoutez des blocs depuis le panneau de gauche</p>
                    <p className="text-sm mt-1">ou utilisez la génération IA</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {contentBlocks.map((block, index) => renderBlockEditor(block, index))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Recipients Tab */}
        <TabsContent value="recipients" className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Sélection des destinataires
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FILTER_TYPES.map((filter) => (
                  <div
                    key={filter.value}
                    className={`p-4 border rounded-lg cursor-pointer transition ${
                      filterType === filter.value
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => {
                      setFilterType(filter.value);
                      setFilterParams({});
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        filterType === filter.value
                          ? 'border-orange-500 bg-orange-500'
                          : 'border-slate-300'
                      }`}>
                        {filterType === filter.value && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="font-medium text-slate-800">{filter.label}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1 ml-6">{filter.description}</p>
                  </div>
                ))}
              </div>

              {/* Filter params based on type */}
              {filterType === 'devis_status' && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label>Statuts de devis à cibler</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[
                      { value: 'brouillon', label: 'Brouillon' },
                      { value: 'envoye', label: 'Envoyé' },
                      { value: 'relance_1', label: 'Relance 1' },
                      { value: 'relance_2', label: 'Relance 2' },
                      { value: 'relance_3', label: 'Relance 3' },
                      { value: 'accepte', label: 'Accepté' },
                      { value: 'refuse', label: 'Refusé' },
                    ].map((status) => (
                      <label key={status.value} className="flex items-center gap-2">
                        <Checkbox
                          checked={(filterParams.statuses || []).includes(status.value)}
                          onCheckedChange={(checked) => {
                            const current = filterParams.statuses || [];
                            setFilterParams({
                              ...filterParams,
                              statuses: checked
                                ? [...current, status.value]
                                : current.filter(s => s !== status.value)
                            });
                          }}
                        />
                        <span className="text-sm">{status.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {filterType === 'contact_types' && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label>Types de contacts</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['client', 'snc', 'fournisseur', 'banque', 'partenaire'].map((type) => (
                      <label key={type} className="flex items-center gap-2">
                        <Checkbox
                          checked={(filterParams.types || []).includes(type)}
                          onCheckedChange={(checked) => {
                            const current = filterParams.types || [];
                            setFilterParams({
                              ...filterParams,
                              types: checked
                                ? [...current, type]
                                : current.filter(t => t !== type)
                            });
                          }}
                        />
                        <span className="text-sm capitalize">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {filterType === 'inactive' && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label>Inactifs depuis (jours)</Label>
                  <Input
                    type="number"
                    value={filterParams.days || 90}
                    onChange={(e) => setFilterParams({ ...filterParams, days: parseInt(e.target.value) })}
                    className="w-32 mt-2"
                    min={7}
                  />
                </div>
              )}

              {filterType === 'new_contacts' && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label>Contacts des derniers (jours)</Label>
                  <Input
                    type="number"
                    value={filterParams.days || 7}
                    onChange={(e) => setFilterParams({ ...filterParams, days: parseInt(e.target.value) })}
                    className="w-32 mt-2"
                    min={1}
                  />
                </div>
              )}

              {filterType === 'manual' && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher un contact..."
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchContacts()}
                    />
                    <Button variant="outline" onClick={handleSearchContacts}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {searchResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border rounded-lg bg-white">
                      {searchResults.map((contact) => (
                        <div
                          key={contact.id}
                          className={`p-2 flex items-center gap-2 cursor-pointer hover:bg-slate-50 ${
                            selectedContacts.includes(contact.id) ? 'bg-orange-50' : ''
                          }`}
                          onClick={() => toggleContactSelection(contact.id)}
                        >
                          <Checkbox checked={selectedContacts.includes(contact.id)} />
                          <div>
                            <p className="text-sm font-medium">{contact.name}</p>
                            <p className="text-xs text-slate-500">{contact.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-sm text-slate-600">
                    {selectedContacts.length} contact(s) sélectionné(s)
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recipients Preview with Selection */}
          {filterType !== 'manual' && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Destinataires
                    {recipientsLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  </div>
                  <Badge className="bg-orange-500 text-white">
                    {recipientsPreview.total - excludedContacts.length} / {recipientsPreview.total}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {excludedContacts.length > 0 && (
                  <div className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="text-sm text-amber-700">
                      {excludedContacts.length} contact(s) exclu(s)
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setExcludedContacts([])}
                      className="text-amber-700 hover:text-amber-900"
                    >
                      Tout réinclure
                    </Button>
                  </div>
                )}
                
                {recipientsPreview.recipients.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={excludedContacts.length === 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setExcludedContacts([]);
                            } else {
                              setExcludedContacts(recipientsPreview.recipients.map(r => r.email));
                            }
                          }}
                        />
                        <span className="text-sm font-medium">Tout sélectionner</span>
                      </label>
                    </div>
                    <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                      {recipientsPreview.recipients.map((r, idx) => {
                        const isExcluded = excludedContacts.includes(r.email);
                        return (
                          <div 
                            key={idx} 
                            className={`p-2 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                              isExcluded ? 'bg-slate-100 opacity-60' : ''
                            }`}
                            onClick={() => {
                              if (isExcluded) {
                                setExcludedContacts(excludedContacts.filter(e => e !== r.email));
                              } else {
                                setExcludedContacts([...excludedContacts, r.email]);
                              }
                            }}
                          >
                            <Checkbox checked={!isExcluded} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isExcluded ? 'line-through' : ''}`}>
                                {r.name}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{r.email}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {recipientsPreview.total > recipientsPreview.recipients.length && (
                      <p className="text-xs text-slate-500 text-center">
                        Affichage limité - {recipientsPreview.total} destinataires au total
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Sparkles className="w-5 h-5 inline mr-2 text-purple-500" />
              Générer avec l'IA
            </DialogTitle>
            <DialogDescription>
              L'IA va créer un email adapté à votre campagne
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Ton de l'email</Label>
              <Select value={aiTone} onValueChange={setAiTone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professionnel">Professionnel</SelectItem>
                  <SelectItem value="amical">Amical</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="promotionnel">Promotionnel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Instructions supplémentaires (optionnel)</Label>
              <Textarea
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                placeholder="Ex: Mentionner une promotion de 10%, insister sur l'urgence..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setAiDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleGenerateWithAI}
                disabled={aiLoading}
              >
                {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                Générer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Send className="w-5 h-5 inline mr-2 text-orange-500" />
              Envoyer la campagne
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">Destinataires :</p>
              <p className="text-2xl font-bold text-slate-800">
                {filterType === 'manual' 
                  ? selectedContacts.length 
                  : recipientsPreview.total - excludedContacts.length
                }
              </p>
              {excludedContacts.length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  ({excludedContacts.length} contact(s) exclu(s))
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <p className="font-medium">Email de test</p>
              <div className="flex gap-2">
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email@test.com"
                />
                <Button
                  variant="outline"
                  onClick={() => handleSend(true)}
                  disabled={sendLoading || !testEmail}
                >
                  {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                className="bg-orange-500 hover:bg-orange-600"
                onClick={() => handleSend(false)}
                disabled={sendLoading}
              >
                {sendLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer à tous
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu de l'email</DialogTitle>
          </DialogHeader>
          <div className="mt-4 border rounded-lg overflow-hidden">
            <div className="bg-slate-100 p-3 border-b">
              <p className="text-sm"><strong>Objet :</strong> {subject || '(Aucun objet)'}</p>
              <p className="text-sm text-slate-500">{previewText}</p>
            </div>
            <div className="p-4 bg-white">
              {contentBlocks.map((block, idx) => (
                <div key={idx} className="mb-3">
                  {block.type === 'text' && (
                    <div dangerouslySetInnerHTML={{ __html: block.content }} />
                  )}
                  {block.type === 'button' && (
                    <div className="text-center">
                      <a
                        href={block.url}
                        style={{ backgroundColor: block.color }}
                        className="inline-block px-6 py-3 text-white rounded-lg font-medium"
                      >
                        {block.text}
                      </a>
                    </div>
                  )}
                  {block.type === 'image' && block.src && (
                    <img src={block.src} alt={block.alt} className="max-w-full rounded" />
                  )}
                  {block.type === 'spacer' && (
                    <div style={{ height: block.height }} />
                  )}
                  {block.type === 'divider' && (
                    <hr className="border-slate-200" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={templatesDialogOpen} onOpenChange={setTemplatesDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              Choisir un template
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un modèle prédéfini pour démarrer rapidement votre campagne
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {templatesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                Aucun template disponible
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <Card 
                    key={template.id}
                    className="cursor-pointer hover:border-orange-400 hover:shadow-md transition-all"
                    onClick={() => applyTemplate(template)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-slate-800">{template.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {template.campaign_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mb-3">{template.description}</p>
                      <div className="p-2 bg-slate-50 rounded text-xs">
                        <p className="font-medium text-slate-600 truncate">
                          📧 {template.subject}
                        </p>
                      </div>
                      {template.variables_used && template.variables_used.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {template.variables_used.slice(0, 4).map((v) => (
                            <Badge key={v} variant="secondary" className="text-xs">
                              {`{${v}}`}
                            </Badge>
                          ))}
                          {template.variables_used.length > 4 && (
                            <Badge variant="secondary" className="text-xs">
                              +{template.variables_used.length - 4}
                            </Badge>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setTemplatesDialogOpen(false)}>
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailEditorPage;
